<template>
  <nav :class="['fixed w-full z-50 transition-all duration-300', isScrolled ? 'glass-nav shadow-lg' : 'bg-teal-800']">
    <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
      <div class="relative flex h-16 items-center justify-between">
        <!-- Mobile menu button -->
        <div class="absolute inset-y-0 left-0 flex items-center sm:hidden">
          <button type="button"
                  class="inline-flex items-center justify-center rounded-md p-2 text-gray-200 hover:bg-white/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors"
                  @click="isMenuOpen = !isMenuOpen">
            <span class="sr-only">Open main menu</span>
            <!-- Hamburger icon -->
            <svg :class="{'block': !isMenuOpen, 'hidden': isMenuOpen}" class="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <!-- Close icon -->
            <svg :class="{'block': isMenuOpen, 'hidden': !isMenuOpen}" class="hidden h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Logo and navigation links -->
        <div class="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
          <div class="flex flex-shrink-0 items-center">
            <img class="h-10 w-auto" :src="require('@/assets/immagini/logo.png')" alt="Pizzoni Viaggi" />
          </div>
          <div class="hidden sm:ml-6 sm:block">
            <div class="flex space-x-4">
              <!-- Navbar links scrolling to sections -->
              <a href="#home" @click.prevent="scrollTo('home')" class="rounded-md px-3 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.home') }}</a>
              <a href="#about" @click.prevent="scrollTo('about')" class="rounded-md px-3 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.about') }}</a>
              <a href="#fleet" @click.prevent="scrollTo('fleet')" class="rounded-md px-3 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.fleet') }}</a>
              <a href="#offers" @click.prevent="scrollTo('offers')" class="rounded-md px-3 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.offers') }}</a>
              <a href="#contact" @click.prevent="scrollTo('contact')" class="rounded-md px-3 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.contact') }}</a>
            </div>
          </div>
        </div>

        <!-- Language switcher + Social icons -->
        <div class="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
          <!-- Language Switcher -->
          <button @click="toggleLanguage" class="hidden sm:flex items-center rounded-md px-2 py-1 text-sm font-medium text-white hover:bg-white/20 transition-colors mr-3">
            <span class="mr-1">🌐</span>
            {{ $i18n.locale.toUpperCase() }}
          </button>

          <!-- Instagram button -->
          <button @click="openInstagram" class="relative rounded-full bg-white/10 p-1.5 text-white hover:bg-white/20 focus:outline-none transition-colors">
            <img class="h-6 w-6 rounded-full" src="https://cdn4.iconfinder.com/data/icons/picons-social/57/38-instagram-2-512.png" alt="Instagram" />
          </button>

          <!-- Facebook button -->
          <div class="relative ml-2">
            <button @click="openFacebook" class="relative flex rounded-full bg-white/10 p-1 text-sm focus:outline-none hover:bg-white/20 transition-colors">
              <img class="h-7 w-7 rounded-full" src="https://static.vecteezy.com/system/resources/thumbnails/018/930/481/small_2x/facebook-logo-facebook-icon-transparent-free-png.png" alt="Facebook" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div :class="['sm:hidden transition-all duration-300 overflow-hidden', isMenuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0']" id="mobile-menu">
      <div class="space-y-1 px-2 pb-3 pt-2">
        <a href="#home" @click.prevent="scrollTo('home')" class="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.home') }}</a>
        <a href="#about" @click.prevent="scrollTo('about')" class="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.about') }}</a>
        <a href="#fleet" @click.prevent="scrollTo('fleet')" class="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.fleet') }}</a>
        <a href="#offers" @click.prevent="scrollTo('offers')" class="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.offers') }}</a>
        <a href="#contact" @click.prevent="scrollTo('contact')" class="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-white/20 transition-colors">{{ $t('nav.contact') }}</a>
        <!-- Language switcher mobile -->
        <button @click="toggleLanguage" class="w-full text-left rounded-md px-3 py-2 text-base font-medium text-white hover:bg-white/20 transition-colors">
          🌐 {{ $i18n.locale === 'it' ? 'English' : 'Italiano' }}
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  data() {
    return {
      isMenuOpen: false,
      isScrolled: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    toggleLanguage() {
      this.$i18n.locale = this.$i18n.locale === 'it' ? 'en' : 'it';
    },
    scrollTo(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        this.isMenuOpen = false;
      }
    },
    openInstagram() {
      window.open('https://www.instagram.com/pizzoniviaggi_official/', '_blank');
    },
    openFacebook() {
      window.open('https://www.facebook.com/pizzoni.official/?locale=it_IT', '_blank');
    }
  }
};
</script>

<style scoped>
</style>
